import { Component } from "@theme/component";
import { VariantSelectedEvent, VariantUpdateEvent } from "@theme/events";
import { morph } from "@theme/morph";

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class NewVariantPicker extends Component {
    /** @type {string | undefined} */
    #pendingRequestUrl;

    /** @type {AbortController | undefined} */
    #abortController;

    connectedCallback() {
        super.connectedCallback();

        this.selectedShape = null;
        this.selectedInsert = null;
        this.templateSuffix = this.dataset.productTemplate_suffix || null;

        this.addEventListener("change", this.variantChanged.bind(this));
        this.withoutAjax = true;
        if (this.templateSuffix == "reserve") {
            this.sizeOptions[0]?.querySelector('input[type="radio"]')?.click();
        }
        //this.setSelectedShape();
    }

    /**
     * Handles the variant change event.
     * @param {Event} event - The variant change event.
     */
    variantChanged(event) {
        // What are we missing?
        // - need to support swatches potentially, we will need to check on what's needed for that.
        if (!(event.target instanceof HTMLElement)) return;
        if (event.target.dataset.inputId.includes("Insert")) {
            const shapeFieldset = this.querySelector(
                '[data-option-name="Shape"]',
            );
            const currentShape = shapeFieldset?.dataset?.selectedShape;
            this.filterSizeByShape(currentShape, event.target.value);
            this.selectFirstVisibleSizeAndUpdatePrice(
                currentShape,
                event.target.value,
            );
            return;
        }
        if (event.target.dataset.inputId.includes("Shape")) {
            const shapeFieldset = this.querySelector(
                '[data-option-name="Shape"]',
            );
            shapeFieldset.dataset.selectedShape = event.target.value;
            this.filterSizeByShape(event.target.value, null);
            this.selectFirstVisibleSizeAndUpdatePrice(event.target.value);
            return;
        }

        this.updateSelectedOption(event.target);
        this.dispatchEvent(
            new VariantSelectedEvent({
                id: event.target.dataset.optionValueId ?? "",
            }),
        );

        const isOnProductPage =
            Theme.template.name === "product" &&
            !event.target.closest("product-card") &&
            !event.target.closest("quick-add-dialog") &&
            !event.target.closest("quick-add-drawer");

        // Morph the entire main content for combined listings child products, because changing the product
        // might also change other sections depending on recommendations, metafields, etc.
        const currentUrl = this.dataset.productUrl?.split("?")[0];
        const newUrl = event.target.dataset.connectedProductUrl;

        const loadsNewProduct =
            isOnProductPage && !!newUrl && newUrl !== currentUrl;

        // Update Variant
        if (this.withoutAjax) {
            const variantId = parseInt(event.target.dataset.variantId, 10);
            if (variantId) {
                const findedVariant =
                    this.currentProduct.product?.variants?.find(
                        (e) => e?.id === variantId,
                    );
                if (!findedVariant) throw new Error("variant not found");
                const optionPosition = event.target?.dataset?.optionPosition;
                const currentOption = this.options.find(
                    (e) => e.dataset.position === optionPosition,
                );
                const selectedOptions = Array.from(
                    this.querySelectorAll("label input:checked"),
                );

                const allOptionInputs =
                    Array.from(this.querySelectorAll("label input")) || [];

                if (selectedOptions?.length <= 0)
                    throw new Error("Selected Options Not Found");
                const variants = this.currentProduct.product?.variants || [];
                const matchingVariants = variants.filter((v) => {
                    return v.options.every(
                        (opt, i) =>
                            !selectedOptions[i] ||
                            v.options[i] === selectedOptions[i]?.value,
                    );
                });
                const variant = matchingVariants[0];
                if (variant) {
                    variant?.options?.forEach((val, index) => {
                        const targetInput = allOptionInputs.find(
                            (e) => e.value === val,
                        );
                        if (targetInput) {
                            targetInput.checked = true;
                            const inputLabel =
                                targetInput.closest(".opt-label");
                            if (inputLabel) {
                                if (variant.available) {
                                    inputLabel.classList.remove(
                                        "variant-option--swatches-disabled",
                                    );
                                    targetInput.dataset.optionAvailable = true;
                                } else {
                                    inputLabel.classList.add(
                                        "variant-option--swatches-disabled",
                                    );
                                    targetInput.dataset.optionAvailable = false;
                                }
                            }
                        }
                    });
                }

                if (currentOption) {
                    const optionLabel = currentOption.querySelector(
                        ".option-selector__label-value",
                    );
                    if (optionLabel) optionLabel.innerHTML = event.target.value;
                    currentOption.dataset.currentValue = event.target.value;
                }

                this.dispatchEvent(
                    new VariantUpdateEvent(null, this.selectedOptionId, {
                        productId: this.dataset.productId ?? "",
                        variant: variant || findedVariant,
                        product: this.currentProduct.product,
                    }),
                );
                const shapeLabel = this?.shapeOption?.querySelector(
                    ".option-selector__label-value",
                );
                const insertLabel = this?.insertOption?.querySelector(
                    ".option-selector__label-value",
                );
                if (shapeLabel)
                    shapeLabel.innerHTML =
                        variant || findedVariant?.metafields?.shape;

                if (insertLabel) {
                    const insertInput = this.insertOption.querySelector(
                        'input[type="radio"]:checked',
                    );
                    insertLabel.innerHTML = insertInput?.dataset?.label || "";
                }
            }
        } else {
            this.fetchUpdatedSection(
                this.buildRequestUrl(event.target),
                loadsNewProduct,
            );
        }

        const url = new URL(window.location.href);

        if (isOnProductPage) {
            if (event.target.dataset.variantId) {
                url.searchParams.set("variant", event.target.dataset.variantId);
            } else {
                url.searchParams.delete("variant");
            }
        }

        // Change the path if the option is connected to another product via combined listing.
        if (loadsNewProduct) {
            url.pathname = newUrl;
        }

        if (url.href !== window.location.href) {
            history.replaceState({}, "", url.toString());
        }
    }

    /**
     * Updates the selected option.
     * @param {string | Element} target - The target element.
     */
    updateSelectedOption(target) {
        if (typeof target === "string") {
            const targetElement = this.querySelector(
                `[data-option-value-id="${target}"]`,
            );

            if (!targetElement) throw new Error("Target element not found");

            target = targetElement;
        }

        if (target instanceof HTMLInputElement) {
            target.checked = true;
        }

        if (target instanceof HTMLSelectElement) {
            const newValue = target.value;
            const newSelectedOption = Array.from(target.options).find(
                (option) => option.value === newValue,
            );

            if (!newSelectedOption) throw new Error("Option not found");

            for (const option of target.options) {
                option.removeAttribute("selected");
            }

            newSelectedOption.setAttribute("selected", "selected");
        }
    }

    /**
     * Builds the request URL.
     * @param {HTMLElement} selectedOption - The selected option.
     * @param {string | null} [source] - The source.
     * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
     * @returns {string} The request URL.
     */
    buildRequestUrl(
        selectedOption,
        source = null,
        sourceSelectedOptionsValues = [],
    ) {
        // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
        // We create a new URL and abort the previous fetch request if it's still pending.
        let productUrl =
            selectedOption.dataset.connectedProductUrl ||
            this.#pendingRequestUrl ||
            this.dataset.productUrl;
        this.#pendingRequestUrl = productUrl;
        const params = [];

        if (this.selectedOptionsValues.length && !source) {
            params.push(
                `option_values=${this.selectedOptionsValues.join(",")}`,
            );
        } else if (source === "product-card") {
            if (this.selectedOptionsValues.length) {
                params.push(
                    `option_values=${sourceSelectedOptionsValues.join(",")}`,
                );
            } else {
                params.push(
                    `option_values=${selectedOption.dataset.optionValueId}`,
                );
            }
        }

        // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
        if (
            this.closest("quick-add-component") ||
            this.closest("swatches-variant-picker-component")
        ) {
            if (productUrl?.includes("?")) {
                productUrl = productUrl.split("?")[0];
            }
            return `${productUrl}?section_id=section-rendering-product-card&${params.join("&")}`;
        }
        return `${productUrl}?${params.join("&")}`;
    }

    /**
     * Fetches the updated section.
     * @param {string} requestUrl - The request URL.
     * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
     */
    fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
        // We use this to abort the previous fetch request if it's still pending.
        this.#abortController?.abort();
        this.#abortController = new AbortController();

        fetch(requestUrl, { signal: this.#abortController.signal })
            .then((response) => response.text())
            .then((responseText) => {
                this.#pendingRequestUrl = undefined;
                const html = new DOMParser().parseFromString(
                    responseText,
                    "text/html",
                );
                // Defer is only useful for the initial rendering of the page. Remove it here.
                html.querySelector("overflow-list[defer]")?.removeAttribute(
                    "defer",
                );

                const textContent = html.querySelector(
                    `new-variant-picker script[type="application/json"]`,
                )?.textContent;
                if (!textContent) return;

                if (shouldMorphMain) {
                    this.updateMain(html);
                } else {
                    const newProduct = this.updateVariantPicker(html);

                    // We grab the variant object from the response and dispatch an event with it.
                    if (this.selectedOptionId) {
                        this.dispatchEvent(
                            new VariantUpdateEvent(
                                JSON.parse(textContent),
                                this.selectedOptionId,
                                {
                                    html,
                                    productId: this.dataset.productId ?? "",
                                    newProduct,
                                },
                            ),
                        );
                    }
                }
            })
            .catch((error) => {
                if (error.name === "AbortError") {
                    console.log("Fetch aborted by user");
                } else {
                    console.error(error);
                }
            });
    }

    /**
     * @typedef {Object} NewProduct
     * @property {string} id
     * @property {string} url
     */

    /**
     * Re-renders the variant picker.
     * @param {Document} newHtml - The new HTML.
     * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
     */
    updateVariantPicker(newHtml) {
        /** @type {NewProduct | undefined} */
        let newProduct;

        const newVariantPickerSource = newHtml.querySelector(
            this.tagName.toLowerCase(),
        );

        if (!newVariantPickerSource) {
            throw new Error("No new variant picker source found");
        }

        // For combined listings, the product might have changed, so update the related data attribute.
        if (newVariantPickerSource instanceof HTMLElement) {
            const newProductId = newVariantPickerSource.dataset.productId;
            const newProductUrl = newVariantPickerSource.dataset.productUrl;

            if (
                newProductId &&
                newProductUrl &&
                this.dataset.productId !== newProductId
            ) {
                newProduct = { id: newProductId, url: newProductUrl };
            }

            this.dataset.productId = newProductId;
            this.dataset.productUrl = newProductUrl;
        }

        morph(this, newVariantPickerSource);

        return newProduct;
    }

    /**
     * Re-renders the entire main content.
     * @param {Document} newHtml - The new HTML.
     */
    updateMain(newHtml) {
        const main = document.querySelector("main");
        const newMain = newHtml.querySelector("main");

        if (!main || !newMain) {
            throw new Error("No new main source found");
        }

        morph(main, newMain);
    }

    /**
     * Gets the selected option.
     * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
     */
    get selectedOption() {
        const selectedOption = this.querySelector(
            "select option[selected], fieldset input:checked:not(.shape-input):not(.insert-input)",
        );

        if (
            !(
                selectedOption instanceof HTMLInputElement ||
                selectedOption instanceof HTMLOptionElement
            )
        ) {
            return undefined;
        }

        return selectedOption;
    }

    /**
     * Gets the selected option ID.
     * @returns {string | undefined} The selected option ID.
     */
    get selectedOptionId() {
        const { selectedOption } = this;
        if (!selectedOption) return undefined;
        const { optionValueId } = selectedOption.dataset;

        if (!optionValueId) {
            throw new Error("No option value ID found");
        }

        return optionValueId;
    }

    /**
     * Gets the selected options values.
     * @returns {string[]} The selected options values.
     */
    get selectedOptionsValues() {
        /** @type HTMLElement[] */
        const selectedOptions = Array.from(
            this.querySelectorAll(
                "select option[selected], fieldset input:checked:not(.shape-input):not(.insert-input)",
            ),
        );

        return selectedOptions.map((option) => {
            const { optionValueId } = option.dataset;

            if (!optionValueId) throw new Error("No option value ID found");

            return optionValueId;
        });
    }

    get shapeOption() {
        return this.querySelector('[data-option-name="Shape"]');
    }

    get insertOption() {
        return this.querySelector('[data-option-name="Insert"]');
    }

    get sizeOptions() {
        return this.querySelectorAll('[data-option-name="Size"] .opt-label');
    }

    get selectedVariant() {
        const { selectedVariant } = this.dataset;
        return selectedVariant;
    }

    get sectionId() {
        return this.dataset.sectionId;
    }

    get initHtml() {
        return this.closest(`[data-section="${this.sectionId}"]`);
    }

    get currentProduct() {
        const jsonData = JSON.parse(
            this.querySelector("[data-product-resource]").innerHTML,
        );
        const productData = jsonData["product"];
        const productTemplate = jsonData["product_template"];
        const productCountry = jsonData["product_country"];
        productData.country = productCountry;
        productData.templateSuffix = productTemplate;

        for (var i = 0; i < productData["variants"].length; i++) {
            var sVariant = jsonData.variants.find(
                (e) => e.variantId === productData.variants[i].id,
            );
            if (sVariant) {
                productData.variants[i].metafields = sVariant.metafields;
            }
        }

        return {
            product: productData,
            productTemplate,
            productCountry,
        };
    }

    get options() {
        return Array.from(this.querySelectorAll(".option-selector"));
    }

    // set shape when variant not selected
    setSelectedShape() {
        if (this.shapeOption) {
            const firstShape = this.shapeOption.querySelector(
                ".option-selector__btns label input",
            );
            const firstInsert = this?.insertOption?.querySelector(
                ".option-selector__btns label input",
            );
            if (!firstShape || this.selectedVariant) return;

            firstShape.value = firstShape.value;
            firstShape.checked = true;
            this.selectedShape = firstShape.value;

            // trigger Change event manually
            firstShape.dispatchEvent(new Event("change", { bubbles: true }));

            if (!firstInsert) return;
            firstInsert.value = firstInsert.value;
            firstInsert.checked = true;
            this.selectedInsert = firstInsert.value;
            firstInsert.dispatchEvent(new Event("change", { bubbles: true }));
        }
    }

    filterSizeByShape(shape, insert) {
        if (this.sizeOptions.length <= 0) return;
        this.selectedShape = shape;
        if (insert) this.selectedInsert = insert;
        shape = shape.replace(" ", "-");
        this.sizeOptions.forEach((sizeLabel) => {
            sizeLabel.style.display = "none";
            if (this.selectedInsert) {
                if (
                    sizeLabel.classList.contains(shape) &&
                    sizeLabel.classList.contains(this.selectedInsert)
                ) {
                    sizeLabel.style.display = "block";
                }
            } else {
                if (sizeLabel.classList.contains(shape)) {
                    sizeLabel.style.display = "block";
                }
            }
        });
    }

    /**
     * Selects the first visible size option and updates price range.
     * @param {string} shape - The selected shape value.
     */
    selectFirstVisibleSizeAndUpdatePrice(shape) {
        // Find the first visible size option
        const visibleSizeLabels = Array.from(this.sizeOptions).filter(
            (label) =>
                label.style.display !== "none" && label.style.display !== "",
        );

        if (visibleSizeLabels.length === 0) return;

        const firstVisibleSizeLabel = visibleSizeLabels[0];
        const firstVisibleSizeInput = firstVisibleSizeLabel.querySelector(
            'input[type="radio"]',
        );

        if (!firstVisibleSizeInput) return;

        setTimeout(() => {
            // Select the first visible size
            firstVisibleSizeInput.checked = true;

            // Trigger change event to update add to cart button and other components
            // The price range will be updated in handleVariantUpdateForPriceRange after VariantUpdateEvent
            //firstVisibleSizeInput?.click();
            firstVisibleSizeInput.dispatchEvent(
                new Event("change", { bubbles: true }),
            );
        }, 50);
    }
}

if (!customElements.get("new-variant-picker")) {
    customElements.define("new-variant-picker", NewVariantPicker);
}
